<script lang="ts">
  import type { App } from '$lib/types';
  import { MetafyPartnerEnum } from '$lib/types/api/graphql';

  import Button from '$mds/Buttons/Button/Button.svelte';

  import SectionDivider from '$mds/Dividers/SectionDivider/SectionDivider.svelte';
  import IconWrapper from '$mds/IconWrapper/IconWrapper.svelte';
  import { faCheck, faCoins } from '@fortawesome/pro-solid-svg-icons';
  import { createEventDispatcher, getContext } from 'svelte';
  import { cubicInOut } from 'svelte/easing';
  import { scale } from 'svelte/transition';

  import BadgeCore from '$assets/svgs/badges/core.svg?component';
  import BadgePro from '$assets/svgs/badges/pro.svg?component';
  import BadgeUltra from '$assets/svgs/badges/ultra.svg?component';
  import Tag from '$mds/Tag/Tag.svelte';
  import { PLANS } from './plans';

  const { irkAccount, isPartner, isPro } = getContext<App>('app');
  const dispatch = createEventDispatcher();

  export let plan: MetafyPartnerEnum = MetafyPartnerEnum.Core;
  export let isYearly = true;
  export let isRecommended = false;

  const CARD_CLASSES = {
    [MetafyPartnerEnum.Core]: 'pricing-card--core',
    [MetafyPartnerEnum.Pro]: 'pricing-card--pro'
    // ultra: 'pricing-card--ultra'
  };

  $: currentPlan = PLANS[plan];
  $: isYearlyAvailable = isYearly && plan !== MetafyPartnerEnum.Core;
  $: isSwitchAvailable =
    $isPro && $irkAccount?.metafyPartnerSubscription?.product?.billingFrequency !== (isYearly ? 'year' : 'month');
</script>

<div class="flex h-full">
  <div
    class="pricing-card flex flex-col rounded-lg p-px {CARD_CLASSES[plan]}"
    class:pricing-card--recommended={isRecommended}
    class:pricing-card--current={currentPlan}
    class:mt-8={!isRecommended}
  >
    {#if isRecommended}
      <div class="-mt-px flex h-8 items-center px-6">
        <span class="text-body-3xs text-ondark-primary">
          {#if $irkAccount?.metafyPartnerType === plan}
            Your current plan
          {:else}
            Recommended
          {/if}
        </span>
      </div>
    {/if}
    <div class="pricing-card__inner flex flex-1 flex-col rounded-lg p-6 pt-5 shadow-lg">
      <div class="flex items-center justify-start gap-4">
        {#if plan === MetafyPartnerEnum.Core}
          <BadgeCore class="size-8 shrink-0" />
        {:else if plan === MetafyPartnerEnum.Pro}
          <BadgePro class="size-8 shrink-0" />
        {:else}
          <BadgeUltra class="size-8 shrink-0" />
        {/if}
        <h3 class="text-ondark-primary text-display-sm flex-1 capitalize">{currentPlan.title}</h3>

        {#if currentPlan.comingSoon}
          <Tag variant="positive" size="md">Coming soon</Tag>
        {/if}
      </div>

      <p class="mt-4 min-h-12">{currentPlan.description}</p>

      <div class="mt-4 flex gap-4">
        <span class="flex flex-col">
          <span class="flex gap-2">
            {#if currentPlan.comingSoon}
              <span class="text-display-sm text-ondark-subtle relative transition-colors">
                <span>TBD</span>
              </span>
            {:else}
              <span
                class="text-display-sm relative transition-colors {isYearlyAvailable
                  ? 'text-ondark-subtle'
                  : 'text-ondark-primary'}"
              >
                <span>${currentPlan.monthly}</span>
                {#if isYearlyAvailable}
                  <span transition:scale|local={{ duration: 300, easing: cubicInOut }}>
                    <span class="bg-onlight-base absolute -inset-x-1 bottom-1/2 h-1 rotate-6 rounded-full shadow" />
                  </span>
                {/if}
              </span>
              {#if isYearlyAvailable}
                <span class="text-positive-ondark-base text-display-sm">${currentPlan.yearly}</span>
              {/if}
            {/if}
          </span>

          <span class="text-body-3xs">per month</span>
        </span>
        <span class="text-body-md text-ondark-subtle mt-2">+</span>
        <span class="flex flex-col">
          <span class="text-ondark-primary text-display-sm">{currentPlan.percentage}%</span>
          <span class="text-body-3xs">of your earnings</span>
        </span>
      </div>

      <Button
        class="mt-6"
        variant={!isRecommended || currentPlan.comingSoon || $irkAccount?.metafyPartnerType === plan
          ? 'secondary'
          : 'pro'}
        disabled={currentPlan.comingSoon || ($irkAccount?.metafyPartnerType === plan && !isSwitchAvailable)}
        width="full"
        on:click={() => dispatch('planClick', { plan, isYearly })}
      >
        {#if currentPlan.comingSoon}
          Coming soon
        {:else if $irkAccount?.metafyPartnerType === plan}
          {#if isSwitchAvailable}
            {#if isYearly}
              Upgrade to Yearly
            {:else}
              Switch to Monthly Billing
            {/if}
          {:else}
            Your current plan
          {/if}
        {:else if $isPartner}
          {#if plan === MetafyPartnerEnum.Core}
            Downgrade to Core
          {:else}
            Upgrade to {currentPlan.title}
          {/if}
        {:else}
          Get started
        {/if}
      </Button>

      <SectionDivider class="mt-6" variant="line" />

      <div class="mt-6 flex flex-1 flex-col gap-4">
        {#if plan !== MetafyPartnerEnum.Core}
          <p class="text-ondark-primary text-body-md">
            Everything in {plan === MetafyPartnerEnum.Pro ? 'Core' : 'Pro'}, plus:
          </p>
        {/if}
        <ul class="flex flex-col gap-4">
          {#each currentPlan.features as [feature, description]}
            <li class="flex gap-3">
              <span
                class="border-ondark-subtle inline-flex size-6 shrink-0 items-center justify-center rounded-full border"
              >
                <IconWrapper icon={faCheck} />
              </span>
              <span class="flex flex-col gap-2">
                <span class="text-ondark-primary">{feature}</span>
                {#if description}
                  <span class="text-ondark-subtle text-body-2xs">{description}</span>
                {/if}
              </span>
            </li>
          {/each}
        </ul>
      </div>

      <SectionDivider class="mt-6" variant="line" />

      <div class="mt-6">
        <p class="text-ondark-primary text-body-md">
          Go <span
            class="capitalize"
            class:text-[#B75BFF]={plan === MetafyPartnerEnum.Pro}
            class:text-[#2C88DD]={plan === MetafyPartnerEnum.Core}
          >
            {currentPlan.title}
          </span> when...
        </p>
        <ul class="mt-4 flex flex-col gap-4">
          <li class="flex gap-3">
            <span
              class="border-ondark-subtle inline-flex size-6 shrink-0 items-center justify-center rounded-full border"
            >
              <IconWrapper icon={faCoins} />
            </span>
            <span class="flex flex-col gap-2">
              <span class="text-ondark-primary">{currentPlan.example.title}</span>
              <span class="text-ondark-subtle text-body-2xs">
                {currentPlan.example.description}
              </span>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</div>

<style lang="postcss">
  .pricing-card {
    --top-color: transparent;
    --border-color: theme('borderColor.ondark.base');
    @apply bg-ondark-base;
  }

  .pricing-card__inner {
    background-color: #0e0e11;
  }

  .pricing-card--recommended {
    --top-color: var(--border-color, transparent);
  }

  .pricing-card[class*='pricing-card--'] {
    background-image:
      linear-gradient(to bottom, var(--top-color, transparent) 32px, transparent 34px),
      linear-gradient(285deg, var(--border-color, grey) 35%, transparent 45%),
      linear-gradient(120deg, var(--border-color, grey) 10%, transparent 35%),
      linear-gradient(theme('borderColor.ondark.subtle'), theme('borderColor.ondark.subtle'));
    background-size: cover;
    background-position: center;
  }

  .pricing-card--core {
    --border-color: #2a5887;
  }

  .pricing-card--core .pricing-card__inner {
    background-image:
      linear-gradient(203.7deg, rgba(44, 136, 221, 0.2) -6.91%, rgba(44, 136, 221, 0) 32.9%),
      linear-gradient(161.46deg, rgba(27, 101, 170, 0.2) -12.91%, rgba(27, 101, 170, 0) 25.18%);
  }

  .pricing-card--pro {
    --border-color: #633588;
  }

  .pricing-card--pro .pricing-card__inner {
    background:
      linear-gradient(203.7deg, rgba(183, 91, 255, 0.2) -6.91%, rgba(183, 91, 255, 0) 32.9%),
      linear-gradient(161.46deg, rgba(153, 23, 255, 0.2) -12.91%, rgba(153, 23, 255, 0) 25.18%), #0e0e11;
  }

  /* .pricing-card--ultra {
    background: linear-gradient(
        111.84deg,
        rgba(221, 147, 24, 0.5) 4.55%,
        rgba(221, 147, 24, 0.05) 28.71%,
        rgba(255, 203, 60, 0.05) 47.54%,
        rgba(255, 190, 10, 0.5) 80.99%
      ),
      #0e0e11;
    .pricing-card__inner {
      background: linear-gradient(203.7deg, rgba(255, 203, 60, 0.2) -6.91%, rgba(255, 203, 60, 0) 32.9%),
        linear-gradient(161.46deg, rgba(221, 147, 24, 0.2) -12.91%, rgba(221, 147, 24, 0) 25.18%), #0e0e11;
    }
  } */
</style>
